import { useEffect, useState } from 'preact/hooks';
import { api } from '@/lib/teaching/teacher-api';
import { errText, type AiSettings } from '@/lib/teaching/slice2';
import { costText, money, type Usage } from '@/lib/teaching/slice6';
import { TeachingPage, Card } from '@/components/teaching/ui';
import { TableCard, thCls, tdCls } from '@/components/teaching/kit';
import { Btn, ErrorLine, Field, inputCls, val } from '@/components/teaching/form';
import { Pill, Note, sentenceCase } from '@/components/teaching/setup';
import { refreshAiReady } from '@/lib/teaching/ai-ready';
import { ChangePasswordCard } from './ChangePassword';

// AI provider, model and the teacher's own keys (§06, S22c, slice 6 §06).
// A key is pasted once, stored encrypted on the server and shown afterwards
// as its last 4 characters only. A provider that obeyed an injected
// instruction is marked unsuitable.

const NAMES: Record<string, string> = { anthropic: 'Anthropic (Claude)', openai: 'OpenAI', fake: 'Fake (test only)' };
const JOB_KIND: Record<string, string> = { identity: 'Name check', mark: 'Marking', recheck: 'Re-check', judgement: 'MYP levels', feedback: 'Feedback' };

type P = AiSettings['providers'][number];

function KeyRow({ p, canSave, onChange }: { p: P; canSave: boolean; onChange: (s: AiSettings) => void }) {
  const [key, setKey] = useState('');
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const name = NAMES[p.id] ?? p.id;
  const saved = p.last4 !== null;

  async function save(e: Event) {
    e.preventDefault(); setErr(null);
    try { onChange(await api<AiSettings>('PUT', `/api/t/settings/keys/${p.id}`, { key })); setKey(''); setOpen(false); }
    catch (e2) { setErr(errText(e2)); }
  }
  async function remove() {
    setErr(null);
    try { onChange(await api<AiSettings>('DELETE', `/api/t/settings/keys/${p.id}`)); } catch (e2) { setErr(errText(e2)); }
  }

  return (
    <div class="rounded-xl border border-[var(--color-border)] p-3 flex flex-col gap-2" data-key-row={p.id}>
      <div class="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span class="text-[13.5px] font-medium text-[var(--color-text)]">{name}</span>
        {saved && !p.needsReentry && <span class="text-[12.5px] text-[#15803d]" data-key-state="saved">Your key ending {p.last4}</span>}
        {saved && p.needsReentry && <span class="text-[12.5px] text-[#b42318]" data-key-state="reenter">Key needs re-entering (it ended {p.last4})</span>}
        {!saved && p.keySource === 'server' && <span class="text-[12.5px] text-[var(--color-text-muted)]" data-key-state="server">Using the key set up on the server</span>}
        {!saved && p.keySource !== 'server' && <span class="text-[12.5px] text-[var(--color-text-faint)]" data-key-state="none">No key yet</span>}
        <span class="ml-auto flex gap-2">
          {!open && <Btn onClick={() => setOpen(true)} disabled={!canSave}><span class="text-[13px]">{saved ? 'Replace key' : `Paste your ${p.id === 'openai' ? 'OpenAI' : 'Anthropic'} key`}</span></Btn>}
          {saved && !open && <Btn kind="danger" onClick={remove}><span class="text-[13px]">Delete key</span></Btn>}
        </span>
      </div>
      {open && (
        <form onSubmit={save} class="flex flex-wrap items-end gap-2">
          <Field label={`${name} API key`} class="flex-1 min-w-[220px]">
            <input class={inputCls} type="password" autocomplete="off" spellcheck={false} value={key} onInput={(e) => setKey(val(e))} placeholder="Paste the whole key" />
          </Field>
          <Btn kind="primary" type="submit" disabled={key.trim().length < 20}><span class="text-[13px]">Save key</span></Btn>
          <Btn onClick={() => { setOpen(false); setKey(''); setErr(null); }}><span class="text-[13px]">Cancel</span></Btn>
        </form>
      )}
      <ErrorLine error={err} />
    </div>
  );
}

function UsageCard({ canRunAgain }: { canRunAgain: boolean }) {
  const [u, setU] = useState<Usage | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = () => api<Usage>('GET', '/api/t/settings/usage').then(setU).catch(() => setU(null));
  useEffect(() => { void load(); }, []);
  /** A job that stopped because there was no key goes back in the queue. */
  async function runAgain(id: string) {
    setErr(null);
    try { await api('POST', `/api/t/jobs/${id}/requeue`); } catch (e) { setErr(errText(e)); }
    await load();
  }
  if (!u) return null;
  const papers = `${u.papers} ${u.papers === 1 ? 'paper' : 'papers'}`;
  const total = u.calls === 0 ? 'nothing spent yet'
    : u.unknown === u.calls ? 'cost unknown'
      : `about ${money(u.cost)}${u.unknown ? `, plus ${u.unknown} ${u.unknown === 1 ? 'call' : 'calls'} with unknown cost` : ''}`;
  return (
    <Card title="AI use and cost" sub="An estimate worked out from the amount of text sent and returned. Your provider's own usage page is the real bill.">
      <div class="text-[15px] text-[var(--color-text)]" data-usage-month>This month: <b>{papers}</b>, <b>{total}</b> <span class="text-[12px] text-[var(--color-text-muted)]">({u.month}, estimate)</span></div>
      {u.jobs.length > 0 && (
        <TableCard class="mt-3">
          <table class="w-full border-collapse" data-usage-jobs>
            <thead><tr><th class={thCls}>When</th><th class={thCls}>Job</th><th class={thCls}>Test</th><th class={thCls}>Size</th><th class={thCls}>Status</th><th class={thCls}>Cost (estimate)</th></tr></thead>
            <tbody>
              {u.jobs.map((j) => (
                <tr key={j.id} class="border-b border-[var(--color-border)] last:border-0">
                  <td class={tdCls + ' whitespace-nowrap'}>{new Date(j.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</td>
                  <td class={tdCls + ' whitespace-nowrap'}>{JOB_KIND[j.kind] ?? j.kind}</td>
                  <td class={tdCls}>{j.test}</td>
                  <td class={tdCls + ' whitespace-nowrap'}>{j.papers ? `${j.papers} ${j.papers === 1 ? 'paper' : 'papers'}` : `${j.students} ${j.students === 1 ? 'student' : 'students'}`}</td>
                  <td class={tdCls + ' whitespace-nowrap'}>
                    {j.error === 'No AI key' ? 'Stopped: no AI key' : sentenceCase(j.status)}
                    {j.error === 'No AI key' && canRunAgain && <button type="button" onClick={() => runAgain(j.id)} class="ml-2 text-[var(--color-accent)]" data-run-again><span class="text-[12.5px] underline">Run again</span></button>}
                  </td>
                  <td class={tdCls + ' whitespace-nowrap'} data-job-cost>{costText(j.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableCard>
      )}
      <ErrorLine error={err} />
    </Card>
  );
}

export function Settings() {
  const [s, setS] = useState<AiSettings | null>(null);
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [unsuitable, setUnsuitable] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const apply = (x: AiSettings) => {
    setS(x); setProvider(x.provider ?? ''); setModel(x.model ?? '');
    setUnsuitable(x.providers.filter((p) => p.unsuitable).map((p) => p.id));
  };
  useEffect(() => { void api<AiSettings>('GET', '/api/t/settings/ai').then(apply).catch((e) => setErr(errText(e))); }, []);

  async function save() {
    setErr(null); setMsg(null);
    try { apply(await api<AiSettings>('PUT', '/api/t/settings/ai', { provider, model: model.trim() || undefined, unsuitable })); setMsg('Saved.'); void refreshAiReady(); }
    catch (e) { setErr(errText(e)); }
  }
  /** A key was saved or deleted: keep the provider choice the teacher has on screen. */
  const keysChanged = (x: AiSettings) => { setS(x); setMsg(null); void refreshAiReady(); };

  const chosen = s?.providers.find((p) => p.id === provider);
  const usable = (id: string) => !!s?.providers.find((p) => p.id === id)?.hasKey && !unsuitable.includes(id);
  return (
    <TeachingPage title="Settings">
      <ErrorLine error={err} />
      {!s && !err && <div class="text-[13px] text-[var(--color-text-muted)]">Loading…</div>}
      {s && (
        <Card title="Your AI keys" sub="Marking suggestions, name checks, MYP level suggestions and feedback drafts run on your own pay-as-you-go key. It is stored encrypted, only its last 4 characters are ever shown again, and you can delete it at any time. Everything else works without AI.">
          <div class="flex flex-col gap-2 max-w-[720px]">
            {!s.canSaveKeys && <Note tone="warn">Keys cannot be saved on this server yet. Ask whoever set up Teacher OS to finish the set-up.</Note>}
            {s.providers.filter((p) => p.id !== 'fake').map((p) => <KeyRow key={p.id} p={p} canSave={s.canSaveKeys} onChange={keysChanged} />)}
          </div>
        </Card>
      )}
      {s && (
        <Card title="AI provider" sub="Choose which one to use once its key is saved.">
          <div class="flex flex-col gap-2 max-w-[720px]" role="radiogroup" aria-label="AI provider">
            {s.providers.map((p) => (
              <div key={p.id} class="rounded-xl border border-[var(--color-border)] p-3 flex flex-wrap items-center gap-3">
                <label class={'flex items-center gap-2 text-[13.5px] text-[var(--color-text)] ' + (usable(p.id) ? 'cursor-pointer' : 'opacity-60')}>
                  <input type="radio" name="provider" value={p.id} checked={provider === p.id} disabled={!usable(p.id)} onChange={() => { setProvider(p.id); setModel(''); }} />
                  {NAMES[p.id] ?? p.id}
                </label>
                {!p.hasKey && <span class="text-[12px] text-[var(--color-text-faint)]">{p.needsReentry ? 'Key needs re-entering' : 'Not connected'}</span>}
                {p.hasKey && <span class="text-[12px] text-[#15803d]">Connected</span>}
                {unsuitable.includes(p.id) && <Pill label="Marked unsuitable" bg="#f8cdcd" />}
                <span class="text-[11.5px] text-[var(--color-text-faint)]">default model {p.defaultModel}</span>
                <label class="ml-auto text-[12px] text-[var(--color-text-muted)] flex items-center gap-1.5">
                  <input type="checkbox" checked={unsuitable.includes(p.id)} onChange={() => setUnsuitable(unsuitable.includes(p.id) ? unsuitable.filter((x) => x !== p.id) : [...unsuitable, p.id])} />
                  Mark unsuitable
                </label>
              </div>
            ))}
            <Field label="Model (optional)">
              <input class={inputCls} value={model} placeholder={chosen?.defaultModel ?? ''} onInput={(e) => setModel(val(e))} />
            </Field>
            {provider && unsuitable.includes(provider) && <Note tone="bad">The chosen provider is marked unsuitable. Pick another before saving.</Note>}
            <div class="flex items-center gap-2">
              <Btn kind="primary" disabled={!provider || !usable(provider)} onClick={save}>Save</Btn>
              {msg && <span class="text-[12.5px] text-[#15803d]">{msg}</span>}
            </div>
            <div class="text-[11.5px] text-[var(--color-text-muted)]">Mark a provider unsuitable when it followed an instruction written in a student's answer.</div>
          </div>
        </Card>
      )}
      {s && <UsageCard canRunAgain={!!s.provider && !!s.providers.find((p) => p.id === s.provider)?.enabled} />}
      <ChangePasswordCard />
    </TeachingPage>
  );
}
